/**
 * PortalPage — casca canônica de TELA do Portal do Cliente (FASE-C).
 *
 * O diagnóstico contou **7 telas do portal sem `<h1>`**. A causa não era falta
 * de título no código: era ordem de renderização. Toda tela fazia
 *
 *   if (!clienteAtivo) return null;
 *   if (query.isLoading) return <p>Carregando…</p>;
 *
 * ANTES de chegar no `<h1>`. Ou seja: enquanto carrega, enquanto o contexto de
 * cliente não resolve e enquanto a permissão é verificada, a tela não tinha
 * cabeçalho nenhum — nem pro leitor de tela, nem pro olho.
 *
 * Aqui o cabeçalho vem PRIMEIRO e o conteúdo é filho. O h1 existe em 100% dos
 * estados (carregando, erro, vazio, cheio), o que resolve a dor 9 na fonte em
 * vez de tela a tela.
 *
 * Usa o `PageHeader` canônico da FASE-B (mesma anatomia do sistema interno:
 * crumb + h1 Oswald + subtítulo + ações), com `crumb` default "Portal IGM" pra
 * o cliente saber sempre onde está.
 */
import type { ReactNode } from 'react';

import { PageHeader } from '@/components/ui';

interface PortalPageProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Migalha acima do título. Default: "Portal IGM". `null` remove. */
  crumb?: string | null;
  /** Botões à direita do título (empilham no 375px). */
  actions?: ReactNode;
  /** `data-testid` do container — preserva os testids já usados nos testes. */
  testId?: string;
  children: ReactNode;
}

export function PortalPage({
  title,
  subtitle,
  crumb = 'Portal IGM',
  actions,
  testId,
  children,
}: PortalPageProps) {
  return (
    /* B-04 (HOTFIX-ROLAGEM): a prop `largura` (leitura=3xl / formulario=2xl)
       era largura própria de tela — o portal tinha 3 réguas concorrendo com a
       do PageContainer da casca. Agora existe UMA régua, a do container. */
    <div data-testid={testId}>
      <PageHeader
        crumb={crumb ?? undefined}
        title={title}
        subtitle={subtitle}
        actions={actions}
      />
      {children}
    </div>
  );
}

/**
 * Estado de carregamento do portal — bloco de placeholders no lugar do texto
 * "Carregando…" solto (que não dizia onde ia aparecer conteúdo e sumia sem
 * aviso). `aria-busy` + texto acessível pro leitor de tela.
 */
export function PortalCarregando({
  linhas = 3,
  rotulo = 'Carregando…',
}: {
  linhas?: number;
  rotulo?: string;
}) {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="portal-carregando">
      <span className="sr-only">{rotulo}</span>
      <div className="space-y-3">
        {Array.from({ length: linhas }).map((_, i) => (
          <div
            key={i}
            className="h-16 rounded-lg border border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface"
          >
            <div className="flex h-full flex-col justify-center gap-2 px-4">
              <div className="h-3 w-2/5 animate-pulse rounded bg-line dark:bg-line-dark" />
              <div className="h-3 w-1/4 animate-pulse rounded bg-line-soft dark:bg-line-soft-dark" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
